/*
 * Copyright 2025 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
package analytics

import models.TimeSeriesPoint
import kotlin.math.*

/**
 * Calculator for determining trend direction in time series data
 * Uses linear regression and statistical analysis to determine if data is increasing, decreasing, or stable
 */
class TrendDirectionCalculator {
    
    /**
     * Calculate the trend direction for a series of data points
     */
    fun calculateTrendDirection(points: List<TimeSeriesPoint>): TrendDirection {
        if (points.isEmpty()) {
            return TrendDirection("NO_DATA", 0.0, 0.0, 0.0)
        }
        
        if (points.size < 2) {
            return TrendDirection("INSUFFICIENT_DATA", 0.0, 0.0, 0.0)
        }
        
        // Convert to numerical arrays for calculation
        val xValues = points.mapIndexed { index, _ -> index.toDouble() }
        val yValues = points.map { it.value }
        
        // Calculate linear regression
        val regression = calculateLinearRegression(xValues, yValues)
        val slope = regression.slope
        val correlation = regression.correlation
        
        // Calculate volatility (standard deviation of residuals)
        val volatility = calculateVolatility(xValues, yValues, regression)
        
        // Determine trend direction based on slope and correlation
        val direction = when {
            abs(correlation) < 0.2 -> "STABLE" // Low correlation means no clear trend
            slope > 1.0 && correlation > 0.3 -> "INCREASING"
            slope < -1.0 && correlation < -0.3 -> "DECREASING"
            else -> "STABLE"
        }
        
        // Calculate confidence based on correlation strength and data consistency
        val confidence = calculateConfidence(correlation, volatility, points.size)
        
        return TrendDirection(direction, confidence, slope, volatility)
    }
    
    private fun calculateLinearRegression(xValues: List<Double>, yValues: List<Double>): LinearRegression {
        val n = xValues.size
        val sumX = xValues.sum()
        val sumY = yValues.sum()
        val sumXY = xValues.zip(yValues) { x, y -> x * y }.sum()
        val sumXX = xValues.sumOf { it * it }
        val sumYY = yValues.sumOf { it * it }
        
        // Calculate slope and intercept
        val slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX)
        val intercept = (sumY - slope * sumX) / n
        
        // Calculate correlation coefficient
        val numerator = n * sumXY - sumX * sumY
        val denominatorX = sqrt(n * sumXX - sumX * sumX)
        val denominatorY = sqrt(n * sumYY - sumY * sumY)
        val correlation = if (denominatorX * denominatorY != 0.0) {
            numerator / (denominatorX * denominatorY)
        } else {
            0.0
        }
        
        return LinearRegression(slope, intercept, correlation)
    }
    
    private fun calculateVolatility(
        xValues: List<Double>, 
        yValues: List<Double>, 
        regression: LinearRegression
    ): Double {
        if (yValues.size < 2) return 0.0
        
        // Calculate residuals (actual - predicted)
        val residuals = xValues.zip(yValues) { x, y ->
            val predicted = regression.slope * x + regression.intercept
            abs(y - predicted)
        }
        
        // Calculate standard deviation of residuals
        val mean = residuals.average()
        val variance = residuals.sumOf { (it - mean).pow(2) } / residuals.size
        val stdDev = sqrt(variance)
        
        // Also consider range and variability relative to mean value
        val yMean = yValues.average()
        val yRange = (yValues.maxOrNull() ?: 0.0) - (yValues.minOrNull() ?: 0.0)
        
        // Return a combination of standard deviation and relative range
        val relativeVariability = if (yMean != 0.0) (yRange / yMean) * 100.0 else 0.0
        
        return max(stdDev, relativeVariability)
    }
    
    private fun calculateConfidence(correlation: Double, volatility: Double, dataPoints: Int): Double {
        // Base confidence on correlation strength
        val correlationConfidence = abs(correlation)
        
        // Reduce confidence for high volatility (but less penalty for good correlations)
        val volatilityPenalty = if (abs(correlation) > 0.8) {
            min(0.1, volatility / 500.0) // Lower penalty for strong correlations
        } else {
            min(0.3, volatility / 200.0) // Higher penalty for weak correlations
        }
        val volatilityAdjusted = correlationConfidence - volatilityPenalty
        
        // Boost confidence for more data points (up to a limit)
        val dataBonus = min(0.15, (dataPoints - 2) * 0.025)
        
        return max(0.0, min(1.0, volatilityAdjusted + dataBonus))
    }
}

/**
 * Result of trend direction calculation
 */
data class TrendDirection(
    val direction: String, // "INCREASING", "DECREASING", "STABLE", "NO_DATA", "INSUFFICIENT_DATA"
    val confidence: Double, // 0.0 to 1.0
    val slope: Double, // Rate of change per time period
    val volatility: Double // Measure of data variability
)

/**
 * Linear regression calculation result
 */
private data class LinearRegression(
    val slope: Double,
    val intercept: Double,
    val correlation: Double
)